import React from "react";
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import { VscFilter } from "react-icons/vsc";
import { fetchUsersApi, deleteUserApi } from "../api/user";
import DeleteItem from "./DeleteItem";
import SideModal from "../../Components/general/Sidemodal";
import { UserForm } from "./UserForm";


const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface UsersProps {
  url: string;
}

export const Users = ({ url }: UsersProps) => {
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [filterModal, setFilterModal] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string } | null>(null);

  const [totalPages, setTotalPages] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("");
  const [search, setSearch] = useState<string>("");


  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchUsersData = async (
    page: number,
    roleType?: string,
    sortColumn?: string,
    sortDirection?: string,
    filterText?: string
  ) => {
    try {
      const response: any = await fetchUsersApi(
        url,
        page,
        itemsPerPage,
        roleType ? parseInt(roleType, 10) : 0,
        sortColumn,
        sortDirection,
        filterText
      );

      if (response) {
        setUsers(response.users);

        const totalCount = response.totalCount || 0;
        setTotalPages(Math.ceil(totalCount / itemsPerPage));
      } else {
        setUsers([]);
        console.error("Invalid response format:", response);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchUsersData(
      currentPage,
      selectedRoleType,
      sortColumn,
      sortDirection,
      search
    );
  }, [currentPage, selectedRoleType, sortColumn, sortDirection, search]);

  useEffect(() => {
    if (debouncedSearchTerm !== search) {
      setSearch(debouncedSearchTerm);
      setCurrentPage(1); 
    }
  }, [debouncedSearchTerm, search]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  const handleNavigation = (_event: React.MouseEvent, _path: string) => {
    setEditUserId(null);
    setEditModalOpen(true);
  };

  const handleEditClick = (
    event: React.MouseEvent,
    _path: string,
    id: string
  ) => {
    event.preventDefault();
    setEditUserId(id);
    setEditModalOpen(true);
  };

  const handleRoleTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedRole = event.target.value;
    setSelectedRoleType(selectedRole);
    setCurrentPage(1);
    fetchUsersData(1, selectedRole);
  };


  const handleDeleteClick = (id: string) => {
    setSelectedItem({ id });
    setIsModalOpen(true);
  };


  const confirmDelete = async () => {
    if (!selectedItem) return;

    try {
      console.log("Deleting user with ID:", selectedItem.id);
      await deleteUserApi(url, selectedItem.id);
      toast.success("User Deleted successfully!");

      fetchUsersData(currentPage, selectedRoleType);
    } catch (error) {
      toast.error("Failed to delete." + error);
    } finally {
      setIsModalOpen(false);
      setSelectedItem(null);
    }
  };



  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => {
      if (prevPage < totalPages) {
        return prevPage + 1;
      }
      return prevPage;
    });
  };

  const handleNameSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("name");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "name", sortDirection);
  };

  const handleEmailSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("email");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "email", sortDirection);
  };

  const handleMobileSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("mobile");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "mobile", sortDirection);
  };

  const handleAddressSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("address");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "address", sortDirection);
  };

  const handleRoleTypeSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("roletype");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "roletype", sortDirection);
  };

  const handleRegisteredOnSortClick = () => {
    let newSortDirection = "";

    if (sortDirection === "") {
      newSortDirection = "asc";
    } else if (sortDirection === "asc") {
      newSortDirection = "desc";
    } else if (sortDirection === "desc") {
      newSortDirection = "asc";
    }
    setSortColumn("createdon");
    setSortDirection(newSortDirection);
    fetchUsersData(currentPage, selectedRoleType, "createdon", sortDirection);
  };

  const refereshUser =() =>{
    fetchUsersData(currentPage, selectedRoleType, sortColumn, sortDirection, search);
  };
  return (
    <div className="px-2 md:px-4 mt-6">
      {/* Header */}

      <div className='flex flex-row items-center '>
        <h1 className="text-2xl md:text-3xl font-bold text-blue-600 mb-5">
          Users
        </h1>

        <button
          type="button"
          className=" w-fit px-3 py-2 mx-3 mb-4 tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
          onClick={(event) => handleNavigation(event, "/admin/users/userform")}
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      {/* Search and Filter */}

      <div className="flex flex-row items-center w-full gap-4 justify-between md:justify-start">
        <div className="w-full md:flex-grow">
          <label
            htmlFor="default-search"
            className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
          >
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="search"
              id="default-search"
              className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Search by Name, Email or Mobile"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div
          className="flex flex-row gap-1 justify-center px-1 py-2 h-10 w-[90px] hover:bg-gray-100 rounded-md cursor-pointer"
          onClick={() => setFilterModal(true)}
        >
          <VscFilter className="text-blue-500 h-7 w-7" />
          <span className="text-lg font-normal text-gray-600">Filter</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-6">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                Actions
              </th>
              <th
                scope="col"
                className="py-3.5 px-4 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Name</span>
                  <button
                    onClick={handleNameSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>

              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Email</span>
                  <button
                    onClick={handleEmailSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Moblie</span>
                  <button
                    onClick={handleMobileSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Address</span>
                  <button
                    onClick={handleAddressSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Gender</span>
                  {/* <button
                    // onClick={handleSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button> */}
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>RoleType</span>
                  <button
                    onClick={handleRoleTypeSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3.5 text-sm md:text-md font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
              >
                <div className="flex items-center justify-between w-full">
                  <span>Registered On</span>
                  <button
                    onClick={handleRegisteredOnSortClick}
                    className="ml-2 text-gray-500 transition-colors duration-200 dark:hover:blue-yellow-500 dark:text-gray-300 hover:text-blue-500 focus:outline-none"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-5 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 8l4-4 4 4M16 16l-4 4-4-4"
                      />
                    </svg>
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
            {users.map((user, index) => (
              <tr key={user.id || index}>
                <td className="px-4 py-4 text-sm whitespace-nowrap">
                  <div className="flex items-center gap-x-6">
                    <button
                      onClick={() => handleDeleteClick(user.id)}
                      className="text-gray-500 transition-colors duration-200 dark:hover:text-red-600 dark:text-gray-300 hover:text-red-500 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={(event) =>
                        handleEditClick(
                          event,
                          "/admin/users/userform?id=",
                          user.id
                        )
                      }
                      className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm font-medium text-gray-700 whitespace-nowrap">
                  <div className="inline-flex items-center gap-x-3">
                    <div className="flex items-center gap-x-2">
                      <div>
                        <h2 className="text-sm font-medium text-gray-800 dark:text-white">
                          {user.name}
                        </h2>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {user.email}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {user.mobile}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {user.address}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {user.gender != null
                    ? user.gender === 1
                      ? "Male"
                      : "Female"
                    : "Not Provided"}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {user.roleType === 1 ? "Guest" : "Admin"}
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap">
                  {new Date(user.createdOn).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
        {/* Previous Button */}
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
            />
          </svg>
          <span>Previous</span>
        </button>

        <div className="flex flex-wrap items-center justify-center gap-x-2">
          {/* Page Number */}

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-2 py-1 text-sm ${currentPage === index + 1
                ? "text-blue-500 bg-blue-100"
                : "text-gray-500 hover:bg-gray-100"
                } rounded-md`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* NEXT Button */}
        <button
          // disabled={currentPage === totalPages}
          // onClick={() => setCurrentPage(currentPage + 1)}
          onClick={handleNextPage}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <span>Next</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
            />
          </svg>
        </button>
      </div>

      {/* Filter Modal */}
      <div className="inset-0 bg-gray-800 bg-opacity-50 z-50 transition-opacity duration-300">
        <div
          className={`fixed top-0 right-0 h-full w-[300px] md:w-[300px] bg-white p-4 rounded-md shadow-lg transform transition-transform duration-500 ease-in-out ${filterModal ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <h1 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4 md:mb-6">
            Filter
          </h1>
          <button
            onClick={() => setFilterModal(false)}
            className="absolute top-1 right-3 text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>

          <div className="flex flex-col">
            <div className="mb-4 flex flex-col">
              <label className="block text-gray-700 mb-2">Role Type</label>
              <div className="w-full">
                <select
                  value={Number(selectedRoleType)}
                  onChange={handleRoleTypeChange}
                  required
                  className="w-full px-4 py-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                >
                  <option value="">All</option>
                  <option value="1">Guest</option>
                  <option value="2">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <DeleteItem onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete} />
      )}

      <SideModal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <UserForm onClose={() => setEditModalOpen(false)} userId={editUserId} url={url} onSaved={refereshUser} />
      </SideModal>

    </div>
  );
};
